import React, { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Smartphone } from "lucide-react";
import { AN_COLORS, AN_FONT } from "../../design-system/tokens";
import { savePendingAppSignIn, validateAppSignInRequest } from "./authenticatorSignIn";

// Shown in the phone's browser: tall, narrow, thumb-sized. One card, no chrome.
const wrap = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 16px",
  background: AN_COLORS.bgWarm || "#F7F7F5",
  fontFamily: AN_FONT,
  boxSizing: "border-box",
};
const card = {
  width: "100%",
  maxWidth: 420,
  background: AN_COLORS.surface || "#fff",
  border: `1px solid ${AN_COLORS.line}`,
  borderRadius: 16,
  padding: "28px 22px",
  textAlign: "center",
  boxSizing: "border-box",
};
const button = {
  display: "block",
  width: "100%",
  marginTop: 22,
  padding: "14px 16px",
  fontSize: 16,
  fontWeight: 600,
  fontFamily: AN_FONT,
  color: "#fff",
  background: AN_COLORS.primary,
  border: "none",
  borderRadius: 12,
  textDecoration: "none",
  cursor: "pointer",
};

export function AppCard({ title, children, action }) {
  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{
          width: 52, height: 52, borderRadius: 14, margin: "0 auto 16px",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "var(--an-primaryBg, #EEF0FF)",
        }}>
          <Smartphone size={26} color={AN_COLORS.primary} />
        </div>
        <h1 style={{ margin: 0, fontSize: 21, fontWeight: 600, color: AN_COLORS.ink, letterSpacing: -0.3 }}>{title}</h1>
        <div style={{ marginTop: 10, fontSize: 15, lineHeight: 1.5, color: AN_COLORS.ink2 }}>{children}</div>
        {action}
      </div>
    </div>
  );
}

/**
 * /authenticator/signin — opened by the AuthNull Authenticator app's
 * "Sign in to your organization". Remembers the app's request, then runs this
 * org's normal sign-in; login.jsx finishes it. See authenticatorSignIn.js.
 */
export default function AuthenticatorSignInPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const problem = useMemo(() => validateAppSignInRequest(params), [params]);

  useEffect(() => {
    if (problem) return;
    savePendingAppSignIn(params);
    // /tenant/login opens the console or this org's SSO, and every path
    // comes back to it with a token.
    navigate("/tenant/login", { replace: true });
  }, [problem, params, navigate]);

  if (problem) {
    return (
      <AppCard title="Can't add this phone">
        {problem} Open the AuthNull Authenticator app and choose <strong>Sign in to your organization</strong> again.
      </AppCard>
    );
  }
  return <AppCard title="Adding your phone">Taking you to your organization's sign-in…</AppCard>;
}

/** The last page: hand back to the app. */
export function AppHandBack({ result }) {
  useEffect(() => {
    // Most browsers follow this straight away; some ask first ("Open in
    // AuthNull?"), and some only allow it from a tap — hence the button too.
    if (result?.url) window.location.href = result.url;
  }, [result]);

  if (!result) {
    return <AppCard title="Adding your phone">Finishing up…</AppCard>;
  }
  return (
    <AppCard
      title={result.ok ? "You're signed in" : "Couldn't add your phone"}
      action={<a href={result.url} style={button}>Return to the AuthNull app</a>}
    >
      {result.ok
        ? "Go back to the AuthNull Authenticator app to finish adding this phone."
        : result.message}
    </AppCard>
  );
}
